import { FC } from 'react';
import type { SopCobertura, SopMes, SopResumoMes } from '@/types/dashboards';
import { descreverCobertura, produzidoBase } from '@/utils/equiplexSop';
import { fmtBRL, fmtDesvio, fmtPct, fmtPreco, fmtUn } from '@/utils/painelFormat';
import { STATUS } from '../chartTheme';
import PainelCard from '../PainelCard';

interface Props {
  resumos: SopResumoMes[];
  meses: SopMes[];
  selecionados: string[];
  cobertura: SopCobertura[];
}

const Faturado: FC<{ r: SopResumoMes; cob: SopCobertura | undefined }> = ({ r, cob }) => {
  if (r.futuro) return <>—</>;
  const incompleto = cob?.status === 'baixo';
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {incompleto && (
        <span
          className="material-symbols-outlined text-[15px] leading-none"
          style={{ color: STATUS.atencao }}
          title={`Faturamento: ${descreverCobertura(cob)}`}
          aria-label="Faturamento abaixo do histórico"
        >
          warning
        </span>
      )}
      {fmtBRL(r.faturado_valor)}
    </span>
  );
};

const Mes: FC<{ r: SopResumoMes; mes: SopMes | undefined; ativo: boolean }> = ({ r, mes, ativo }) => (
  <>
    <div className={ativo ? 'font-medium text-content-primary' : ''}>
      {r.label}
      {r.em_andamento && <span className="ml-1 text-2xs text-content-muted">(em andamento)</span>}
      {r.futuro && <span className="ml-1 text-2xs text-content-muted">(futuro)</span>}
    </div>
    <div className="text-xs text-content-muted">{mes?.range}</div>
  </>
);

const Programado: FC<{ r: SopResumoMes }> = ({ r }) => (
  <>
    {fmtUn(r.futuro ? r.programado : r.programado_base)}
    {r.em_andamento && (
      <div className="text-xs text-content-muted" title="Programado no mês inteiro">
        de {fmtUn(r.programado)}
      </div>
    )}
  </>
);

/** No mês aberto, o produzido das OPs dos dias fechados (o que entra no desvio); o total do mês embaixo. */
const Produzido: FC<{ r: SopResumoMes }> = ({ r }) => (
  <>
    {fmtUn(produzidoBase(r))}
    {r.em_andamento && r.produzido_base !== undefined && (
      <div className="text-xs text-content-muted" title="Produzido de todas as OPs do mês até agora">
        de {fmtUn(r.produzido)}
      </div>
    )}
  </>
);

/** Resumo mês a mês (todos os meses do snapshot; os do recorte ficam destacados). */
const ResumoMensalTable: FC<Props> = ({ resumos, meses, selecionados, cobertura }) => {
  const porChave = Object.fromEntries(meses.map((m) => [m.key, m]));
  const cobPorMes = Object.fromEntries(cobertura.map((c) => [c.mes, c]));

  return (
    <PainelCard testId="sop-resumo-mensal" titulo="Resumo por mês" subtitulo="Mês de produção de 16 a 15. Todos os meses do snapshot. No mês em andamento, programado e produzido só das OPs dos dias fechados; o total do mês fica embaixo.">
      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr className="border-b border-theme-border text-left text-2xs uppercase tracking-wide text-content-muted">
              <th className="py-2 pr-3 font-medium">Mês</th>
              <th className="py-2 pr-3 text-right font-medium">Programado</th>
              <th className="py-2 pr-3 text-right font-medium">Produzido</th>
              <th className="py-2 pr-3 text-right font-medium">Desvio</th>
              <th className="py-2 pr-3 text-right font-medium">Aderência</th>
              <th className="py-2 pr-3 text-right font-medium" title="SKUs acima / abaixo / no plano">
                SKUs ↑ / ↓ / =
              </th>
              <th className="py-2 pr-3 text-right font-medium">Crít. / alerta</th>
              <th className="py-2 pr-3 text-right font-medium" title="Lotes que entraram no CQ e ainda não foram liberados">
                Lotes no CQ
              </th>
              <th className="py-2 pr-3 text-right font-medium">Faturado</th>
              <th className="py-2 text-right font-medium">Preço médio</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {resumos.map((r) => {
              const ativo = selecionados.includes(r.mes);
              return (
                <tr
                  key={r.mes}
                  className={`border-b border-theme-border ${ativo ? '' : 'text-content-muted'}`}
                  data-testid={`resumo-${r.mes}`}
                >
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <Mes r={r} mes={porChave[r.mes]} ativo={ativo} />
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <Programado r={r} />
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <Produzido r={r} />
                  </td>
                  <td className="py-2 pr-3 text-right font-medium">{fmtDesvio(r.desvio_pct)}</td>
                  <td className="py-2 pr-3 text-right">{fmtPct(r.aderencia_pct)}</td>
                  <td className="py-2 pr-3 text-right">
                    {r.skus_acima} / {r.skus_abaixo} / {r.skus_no_plano}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {r.criticos} / {r.alertas}
                  </td>
                  <td className="py-2 pr-3 text-right">{r.lotes_pendentes_cq ? fmtUn(r.lotes_pendentes_cq) : '—'}</td>
                  <td className="py-2 pr-3 text-right">
                    <Faturado r={r} cob={cobPorMes[r.mes]} />
                  </td>
                  <td className="py-2 text-right">{fmtPreco(r.pmv_realizado)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PainelCard>
  );
};

export default ResumoMensalTable;
